import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import BottomDrawer from '@/components/shared/BottomDrawer';
import CourseOptionCard from '@/components/student-dashboard/CourseOptionCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import InfinityList from '@/components/ui/custom/InfinityList';
import ComingSoonTooltip from '@/components/ui/custom/dataDisplay/ComingSoonTooltip';
import { Tabs } from '@/components/ui/daisy';
import { useModalDialog } from '@/components/ui/daisy/modal/hooks';
import { useStudentDashboard } from '@/data/context/StudentDashboardContext';
import { Breakpoints } from '@/utils/Breakpoint';
import DesktopWrapper from './DesktopWrapper';

export enum CourseSelectTabEnum {
  MY_COURSES = 'myCourses',
  EXPLORE = 'explore',
}

type CourseSelectDrawerProps = {
  isOpen: boolean;
  onClose(): void;
};

export default function CourseSelectDrawer({
  isOpen,
  onClose,
}: CourseSelectDrawerProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.courseSelect',
  });

  const isMd = useMediaQuery({ minWidth: Breakpoints.md });
  const {
    courseProgresses,
    selectCourseProgress,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useStudentDashboard();
  const [tab, setTab] = useState(CourseSelectTabEnum.MY_COURSES);
  const { ref, close } = useModalDialog(isOpen, onClose);

  const handleTabChange = (value: string) => {
    if (value === CourseSelectTabEnum.EXPLORE) return;
    setTab(value as CourseSelectTabEnum);
  };

  const handleSelect = (courseProgressId: number) => {
    selectCourseProgress(courseProgressId);
    if (isMd) onClose();
    else close();
  };

  const content = (
    <Fragment>
      <Tabs
        variant="box"
        size="sm"
        className="w-fit"
        value={tab}
        onChange={handleTabChange}
      >
        <Tabs.Tab value={CourseSelectTabEnum.MY_COURSES}>
          {t('myCourses')}
        </Tabs.Tab>
        <ComingSoonTooltip disabled className="flex">
          <Tabs.Tab value={CourseSelectTabEnum.EXPLORE}>
            {t('explore')}
          </Tabs.Tab>
        </ComingSoonTooltip>
      </Tabs>
      <h3 className="text-sm font-semibold text-base-content">
        {t('myCourses')}
      </h3>
      <InfinityList
        className="h-auto gap-3"
        onReachEnd={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      >
        {courseProgresses.map(courseProgress => (
          <CourseOptionCard
            key={courseProgress.id}
            courseProgress={courseProgress}
            onSelect={handleSelect}
          />
        ))}
      </InfinityList>
    </Fragment>
  );

  return (
    <ConditionalRenderer
      condition={isMd}
      fallback={
        <BottomDrawer dialogRef={ref} isOpen={isOpen} onClose={close}>
          {content}
        </BottomDrawer>
      }
    >
      <DesktopWrapper isOpen={isOpen} onClose={onClose}>
        {content}
      </DesktopWrapper>
    </ConditionalRenderer>
  );
}
